import {
  Pagination as PaginationRoot,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'designSystem/components/Pagination'
import { Ellipsis } from 'lucide-react'
import React from 'react'

const MAX_PAGE_BUTTONS = 10

const PaginationButton: React.FC<{
  i: number
  content?: React.ReactNode
  label?: string
  tableCurrentPage: number
  tableGetToPage: (i: number) => void
}> = ({ i, content, label, tableCurrentPage, tableGetToPage }) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    tableGetToPage(i)
  }

  return (
    <PaginationItem>
      <PaginationLink
        href='#'
        aria-label={label ?? `Go to page ${i + 1}`}
        isActive={tableCurrentPage === i}
        size='icon-lg'
        onClick={handleClick}>
        {content ?? i + 1}
      </PaginationLink>
    </PaginationItem>
  )
}

export const Pagination: React.FC<{
  tablePreviousPage: () => void
  tableNextPage: () => void
  getCanNextPage: () => boolean
  getCanPreviousPage: () => boolean
  tablePagesCount: number
  tableGetToPage: (i: number) => void
  tableCurrentPage: number
}> = ({
  tablePreviousPage,
  tableNextPage,
  getCanNextPage,
  getCanPreviousPage,
  tablePagesCount,
  tableGetToPage,
  tableCurrentPage,
}) => {
  const getDisabledLinkProps = (disabled: boolean) => ({
    'aria-disabled': disabled || undefined,
    tabIndex: disabled ? -1 : undefined,
  })

  const paginationButtons = () => {
    const buttons: React.ReactNode[] = []

    const maxButtons = MAX_PAGE_BUTTONS
    const totalPages = tablePagesCount
    const currentPage = tableCurrentPage + 1 // 1-based
    const centerIndex = 5 // for maxButtons=10, center at index 5 (6th button)

    if (totalPages === -1) {
      buttons.push(
        <PaginationButton
          i={tableCurrentPage}
          key={tableCurrentPage}
          tableCurrentPage={tableCurrentPage}
          tableGetToPage={tableGetToPage}
        />
      )

      return buttons
    }

    // Always show first page
    buttons.push(
      <PaginationButton
        i={0}
        key={0}
        tableCurrentPage={tableCurrentPage}
        tableGetToPage={tableGetToPage}
      />
    )

    if (totalPages <= maxButtons) {
      // Show all pages
      for (let i = 1; i < totalPages - 1; i++) {
        buttons.push(
          <PaginationButton
            i={i}
            key={i}
            tableCurrentPage={tableCurrentPage}
            tableGetToPage={tableGetToPage}
          />
        )
      }
    } else {
      // Left-anchored window
      if (currentPage <= centerIndex + 1) {
        for (let i = 1; i < maxButtons - 2; i++) {
          buttons.push(
            <PaginationButton
              i={i}
              key={i}
              tableCurrentPage={tableCurrentPage}
              tableGetToPage={tableGetToPage}
            />
          )
        }
        // Ellipsis (jump to first truncated page after the last shown)
        buttons.push(
          <PaginationButton
            key={'ellipsis-end'}
            content={<Ellipsis aria-hidden='true' />}
            label={`Jump to page ${maxButtons - 1}`}
            i={maxButtons - 2}
            tableCurrentPage={tableCurrentPage}
            tableGetToPage={tableGetToPage}
          />
        )
      }

      // Right-anchored window
      else if (currentPage >= totalPages - (maxButtons - centerIndex - 1)) {
        // Ellipsis (jump to first truncated page before the first shown)
        buttons.push(
          <PaginationButton
            key={'ellipsis-start'}
            content={<Ellipsis aria-hidden='true' />}
            label={`Jump to page ${totalPages - (maxButtons - 2)}`}
            i={totalPages - (maxButtons - 2) - 1}
            tableCurrentPage={tableCurrentPage}
            tableGetToPage={tableGetToPage}
          />
        )
        for (let i = totalPages - (maxButtons - 2); i < totalPages - 1; i++) {
          buttons.push(
            <PaginationButton
              i={i}
              key={i}
              tableCurrentPage={tableCurrentPage}
              tableGetToPage={tableGetToPage}
            />
          )
        }
      }

      // Centered window
      else {
        // Left ellipsis (jump to first truncated page after 1)
        buttons.push(
          <PaginationButton
            key={'ellipsis-start'}
            content={<Ellipsis aria-hidden='true' />}
            label={`Jump to page ${currentPage - centerIndex + 1}`}
            i={currentPage - centerIndex}
            tableCurrentPage={tableCurrentPage}
            tableGetToPage={tableGetToPage}
          />
        )
        // Middle window
        const start = currentPage - centerIndex + 1
        for (let i = 0; i < maxButtons - 4; i++) {
          const page = start + i
          buttons.push(
            <PaginationButton
              i={page}
              key={page}
              tableCurrentPage={tableCurrentPage}
              tableGetToPage={tableGetToPage}
            />
          )
        }
        // Right ellipsis (jump to first truncated page before N)
        buttons.push(
          <PaginationButton
            key={'ellipsis-end'}
            content={<Ellipsis aria-hidden='true' />}
            label={`Jump to page ${start + maxButtons - 3}`}
            i={start + maxButtons - 4}
            tableCurrentPage={tableCurrentPage}
            tableGetToPage={tableGetToPage}
          />
        )
      }
    }

    // Always show last page (if not already shown)
    if (totalPages > 1) {
      buttons.push(
        <PaginationButton
          i={totalPages - 1}
          key={totalPages - 1}
          tableCurrentPage={tableCurrentPage}
          tableGetToPage={tableGetToPage}
        />
      )
    }

    return buttons
  }

  const previousDisabled = !getCanPreviousPage()
  const nextDisabled = !getCanNextPage()

  return (
    <PaginationRoot>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href='#'
            size='lg'
            {...getDisabledLinkProps(previousDisabled)}
            onClick={(event) => {
              event.preventDefault()
              if (!previousDisabled) tablePreviousPage()
            }}
          />
        </PaginationItem>
        {paginationButtons()}
        <PaginationItem>
          <PaginationNext
            href='#'
            size='lg'
            {...getDisabledLinkProps(nextDisabled)}
            onClick={(event) => {
              event.preventDefault()
              if (!nextDisabled) tableNextPage()
            }}
          />
        </PaginationItem>
      </PaginationContent>
    </PaginationRoot>
  )
}
